import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {fetchData, toastsuccessmsg, toasterrormsg, getTranslatedValue, getLocalizedName} from '../../../../reusable'
import {getSelectedLang} from '../../../../_metronic/i18n/Metronici18n'
import {Tr} from '../../../../_metronic/i18n/Tr'
import {ToastContainer} from 'react-toastify'

interface CategoryConfig {
  categoryId: string
  actionType: string
  parentLabel: string
  childLabel: string
  parentApi: string
  parentParam: (vendorId: string) => object
  parentIdKey: string
  childApi: string
  childParam: (parentId: string) => object
  childListKey: string
  childIdKey: string
  allowWholeSelection: boolean
  wholeActionType: string
  icon: string
  color: string
  bgColor: string
  description: string
}

const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  '1': {
    categoryId: '1',
    actionType: 'room',
    parentLabel: 'Hotel',
    childLabel: 'Rooms',
    parentApi: 'public/hotelList',
    parentParam: (vendorId) => ({vendorId}),
    parentIdKey: 'hotelId',
    childApi: 'public/hotelDetailsV2',
    childParam: (parentId) => ({hotelId: parentId}),
    childListKey: 'roomList',
    childIdKey: 'roomId',
    allowWholeSelection: true,
    wholeActionType: 'hotel',
    icon: 'fas fa-hotel',
    color: '#6366f1',
    bgColor: '#eef2ff',
    description: 'Hotels & Resorts',
  },
  '3': {
    categoryId: '3',
    actionType: 'restaurant',
    parentLabel: 'Restaurant',
    childLabel: 'Restaurants',
    parentApi: 'public/restaurantList',
    parentParam: (vendorId) => ({vendorId: String(vendorId)}),
    parentIdKey: 'restaurantId',
    childApi: 'public/restaurantDetail',
    childParam: (parentId) => ({restaurantId: String(parentId)}),
    childListKey: 'restaurantmenu',
    childIdKey: 'menuId',
    allowWholeSelection: true,
    wholeActionType: 'restaurant',
    icon: 'fas fa-utensils',
    color: '#f97316',
    bgColor: '#fff7ed',
    description: 'Cafes & Restaurants',
  },
  '4': {
    categoryId: '4',
    actionType: 'space',
    parentLabel: 'Space Rental',
    childLabel: 'Rentable Spaces',
    parentApi: 'public/spacerentalList',
    parentParam: (vendorId) => ({vendorId}),
    parentIdKey: 'spacerentalId',
    childApi: 'public/spaceDetails',
    childParam: (parentId) => ({spacerentalId: parentId}),
    childListKey: 'rentablespace',
    childIdKey: 'rentablespaceId',
    allowWholeSelection: true,
    wholeActionType: 'space',
    icon: 'fas fa-building',
    color: '#8b5cf6',
    bgColor: '#f5f3ff',
    description: 'Spaces & Venues',
  },
  '5': {
    categoryId: '5',
    actionType: 'tours',
    parentLabel: 'Tour Package',
    childLabel: 'Tours',
    parentApi: 'public/tourpackageList',
    parentParam: (vendorId) => ({vendorId}),
    parentIdKey: 'tourpackageId',
    childApi: 'public/tourpackageDetails',
    childParam: (parentId) => ({tourpackageId: parentId}),
    childListKey: 'toursDetail',
    childIdKey: 'toursId',
    allowWholeSelection: false,
    wholeActionType: 'tourpackage',
    icon: 'fas fa-map-marked-alt',
    color: '#059669',
    bgColor: '#ecfdf5',
    description: 'Tours & Experiences',
  },
  '7': {
    categoryId: '7',
    actionType: 'vehicle',
    parentLabel: 'Car Rental',
    childLabel: 'Vehicles',
    parentApi: 'public/carRentalList',
    parentParam: (vendorId) => ({vendorId: String(vendorId)}),
    parentIdKey: 'carrentalId',
    childApi: 'public/carRentalDetail',
    childParam: (parentId) => ({carrentalId: String(parentId)}),
    childListKey: 'vehcileDetail',
    childIdKey: 'vehicleId',
    allowWholeSelection: false,
    wholeActionType: 'carrental',
    icon: 'fas fa-car',
    color: '#0284c7',
    bgColor: '#e0f2fe',
    description: 'Car Rentals',
  },
}

// name in the selected language, falling back to the listing's primary language
const getDisplayName = (item: any): string =>
  getLocalizedName(item, getSelectedLang()) || `#${Object.values(item || {})[0] || ''}`

const getThumbImage = (item: any): string | null => {
  if (Array.isArray(item?.images) && item.images.length > 0) {
    const src = item.images[0]?.thumbPath || item.images[0]?.mediaPath || null
    if (src && !src.includes('default.png')) return src
  }
  if (item?.image && typeof item.image === 'string' && !item.image.includes('default.png')) {
    return item.image
  }
  return null
}

const getSubcategoryName = (item: any): string | null => {
  if (Array.isArray(item?.subcategory) && item.subcategory.length > 0) {
    const sub = item.subcategory[0]
    return getTranslatedValue(getSelectedLang(), sub, '_name')?.trim() || sub?.en_name?.trim() || null
  }
  return null
}

interface AddListingAdsProps {
  onSaveSuccess?: (sponserId?: string) => void
}

const AddListingAds = ({onSaveSuccess}: AddListingAdsProps) => {
  const t = Tr()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState =
    (location.state as {adsId?: string | number; adsData?: any; tab?: number} | null) || null

  const vendorData = sessionStorage.getItem('vendor')
  const vendorId = vendorData ? String(JSON.parse(vendorData).vendorId || '') : ''
  const adsId = locationState?.adsId ? String(locationState.adsId) : ''
  const adsData = locationState?.adsData || null

  const [parentLists, setParentLists] = useState<Record<string, any[]>>({})
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const [selectedParentId, setSelectedParentId] = useState('')
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null)
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([])
  const [childList, setChildList] = useState<any[]>([])
  const [savedActionTypeIds, setSavedActionTypeIds] = useState<string[]>([])
  const [isLoadingParents, setIsLoadingParents] = useState(false)
  const [isLoadingChildren, setIsLoadingChildren] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)
  const [hoveredParent, setHoveredParent] = useState<string | null>(null)
  const restoreDoneRef = useRef(false)

  const availableCategoryIds = useMemo(
    () =>
      Object.keys(parentLists).filter(
        (id) => Array.isArray(parentLists[id]) && parentLists[id].length > 0
      ),
    [parentLists]
  )

  const selectedConfig = selectedCategoryId ? CATEGORY_CONFIG[selectedCategoryId] : null
  const selectedParentList = selectedCategoryId ? parentLists[selectedCategoryId] || [] : []

  useEffect(() => {
    if (!vendorId) return
    let isMounted = true
    const loadParents = async () => {
      setIsLoadingParents(true)
      try {
        const results = await Promise.all(
          Object.entries(CATEGORY_CONFIG).map(async ([categoryId, config]) => {
            try {
              const res: any = await fetchData(config.parentApi, config.parentParam(vendorId))
              return [categoryId, String(res?.status) === '200' ? res.data || [] : []] as const
            } catch {
              return [categoryId, []] as const
            }
          })
        )
        if (!isMounted) return
        const next = results.reduce<Record<string, any[]>>((acc, [id, list]) => {
          acc[id] = Array.isArray(list) ? list : []
          return acc
        }, {})
        setParentLists(next)
      } finally {
        if (isMounted) setIsLoadingParents(false)
      }
    }
    loadParents()
    return () => {
      isMounted = false
    }
  }, [vendorId])

  useEffect(() => {
    if (!Object.keys(parentLists).length || restoreDoneRef.current) return
    if (!adsData?.actionType || adsData.actionTypeId == null) return

    const {actionType, actionTypeId} = adsData

    for (const [categoryId, config] of Object.entries(CATEGORY_CONFIG)) {
      if (config.wholeActionType === actionType) {
        // Whole-parent selection (e.g. hotel, spacerental)
        restoreDoneRef.current = true
        setSelectedCategoryId(categoryId)
        setSelectedParentId(String(actionTypeId))
        setSelectionMode('whole')
        return
      }
      if (config.actionType === actionType) {
        // Specific-child selection (e.g. room, vehicle, tour)
        const ids = Array.isArray(actionTypeId)
          ? actionTypeId.map(String)
          : [String(actionTypeId)]
        setSavedActionTypeIds(ids)
        return
      }
    }
  }, [adsData, parentLists])

  useEffect(() => {
    if (adsData || !adsId || !vendorId) return
    const loadExistingAd = async () => {
      try {
        const res: any = await fetchData('public/getad', {
          vendorId: Number(vendorId),
          adsId: Number(adsId),
        })
        if (String(res?.status) === '200') {
          const ids = res?.data?.actionTypeId
          if (ids != null) {
            setSavedActionTypeIds(Array.isArray(ids) ? ids.map(String) : [String(ids)])
          }
        }
      } catch (error) {
        console.error('Failed to fetch sponsored ad detail', error)
      }
    }
    loadExistingAd()
  }, [adsId, vendorId])

  useEffect(() => {
    if (selectedCategoryId) return
    if (availableCategoryIds.length !== 1) return
    setSelectedCategoryId(availableCategoryIds[0])
  }, [availableCategoryIds, selectedCategoryId])

  useEffect(() => {
    if (!selectedConfig || !selectedParentId || selectionMode === 'whole') {
      setChildList([])
      return
    }
    let isMounted = true
    const loadChildren = async () => {
      setIsLoadingChildren(true)
      try {
        const res: any = await fetchData(
          selectedConfig.childApi,
          selectedConfig.childParam(selectedParentId)
        )
        if (!isMounted) return
        if (String(res?.status) === '200') {
          const children = res.data?.[0]?.[selectedConfig.childListKey] || []
          setChildList(children)
          setSelectedChildIds([])
        } else {
          setChildList([])
          setSelectedChildIds([])
        }
      } finally {
        if (isMounted) setIsLoadingChildren(false)
      }
    }
    loadChildren()
    return () => {
      isMounted = false
    }
  }, [selectedCategoryId, selectedParentId, selectedConfig, selectionMode])

  useEffect(() => {
    if (restoreDoneRef.current) return
    if (!savedActionTypeIds.length) return
    if (!availableCategoryIds.length) return
    const restoreSelection = async () => {
      restoreDoneRef.current = true
      for (const categoryId of availableCategoryIds) {
        const config = CATEGORY_CONFIG[categoryId]
        const parents = parentLists[categoryId] || []
        for (const parent of parents) {
          const parentId = String(parent[config.parentIdKey])
          try {
            const res: any = await fetchData(config.childApi, config.childParam(parentId))
            if (String(res?.status) !== '200') continue
            const children = res.data?.[0]?.[config.childListKey] || []
            const childIds = children.map((child: any) => String(child[config.childIdKey]))
            const matched = savedActionTypeIds.filter((id) => childIds.includes(id))
            if (matched.length > 0) {
              setSelectedCategoryId(categoryId)
              setSelectedParentId(parentId)
              setSelectionMode('specific')
              setSelectedChildIds([matched[0]])
              return
            }
          } catch {}
        }
      }
    }
    restoreSelection()
  }, [savedActionTypeIds, availableCategoryIds, parentLists])

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId)
    setSelectedParentId('')
    setSelectedChildIds([])
    setChildList([])
    setSelectionMode(null)
  }

  const handleParentSelect = (parentId: string) => {
    if (selectedParentId === parentId) {
      setSelectedParentId('')
      setSelectedChildIds([])
      setChildList([])
      setSelectionMode(null)
      return
    }
    setSelectedParentId(parentId)
    setSelectedChildIds([])
    if (selectedConfig?.allowWholeSelection) {
      setSelectionMode('whole')
    } else {
      setSelectionMode('specific')
    }
  }

  const handleChildToggle = (childId: string) => {
    setSelectedChildIds((prev) => (prev[0] === childId ? [] : [childId]))
  }

  const handleSubmit = async () => {
    if (!vendorId) {
      toasterrormsg(t('Vendor not found. Please login again.'))
      return
    }
    if (!selectedConfig) {
      toasterrormsg(t('Please select a business type.'))
      return
    }
    if (!selectedParentId) {
      toasterrormsg(`${t('Please select')} ${t(selectedConfig.parentLabel)}`)
      return
    }

    if (selectionMode === 'specific' && selectedChildIds.length === 0) {
      toasterrormsg(
        `${t('Please select at least one')} ${t(selectedConfig.childLabel.toLowerCase())}`
      )
      return
    }

    const finalActionType =
      selectionMode === 'whole' ? selectedConfig.wholeActionType : selectedConfig.actionType
    const finalActionTypeIds =
      selectionMode === 'whole'
        ? [Number(selectedParentId)]
        : selectedChildIds.map(Number)

    setIsSubmitting(true)
    try {
      const sponserId = adsData?.sponserId || (adsId ? Number(adsId) : null)
      const payload: Record<string, any> = {
        vendorId: Number(vendorId),
        actionType: finalActionType,
        actionTypeId: finalActionTypeIds,
      }
      if (sponserId) {
        payload.sponserId = Number(sponserId)
      }
      const res: any = await fetchData('public/addeditsponseraction', payload)
      if (String(res?.status) === '200') {
        toastsuccessmsg(res?.message || t('Listing selection saved successfully'))
        const savedAdsId = res?.data?.sponserId
          ? String(res.data.sponserId)
          : res?.data?.adsId
            ? String(res.data.adsId)
            : adsId
        navigate(location.pathname, {
          replace: true,
          state: {...locationState, adsId: savedAdsId},
        })
        onSaveSuccess?.(savedAdsId)
      } else {
        toasterrormsg(res?.message || t('Failed to save listing selection'))
      }
    } catch (error) {
      console.error('Save listing action error:', error)
      toasterrormsg(t('Failed to save listing selection'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Step progress calculation
  const step1Done = !!selectedCategoryId
  const step2Done = !!selectedParentId
  const step3Done = selectionMode === 'whole' || selectedChildIds.length > 0

  return (
    <>
      <ToastContainer />
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.3); }
          70% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
          100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
        }
        .listing-ads-cat-card { transition: all 0.22s ease; }
        .listing-ads-cat-card:hover { transform: translateY(-3px); }
        .listing-ads-parent-card { transition: all 0.22s ease; }
        .listing-ads-parent-card:hover .listing-ads-parent-img { transform: scale(1.06); }
        .listing-ads-child-card { transition: all 0.18s ease; }
        .listing-ads-child-card:hover { transform: translateY(-2px); }
      `}</style>

      <div
        style={{
          background: 'white',
          borderRadius: 18,
          boxShadow: '0 4px 30px rgba(0,0,0,0.07)',
          overflow: 'hidden',
        }}
      >

        {/* Body */}
        <div style={{padding: '28px 32px'}}>

          {isLoadingParents ? (
            <div className='d-flex flex-column align-items-center py-16 gap-3'>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: '#eef2ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse-ring 1.5s infinite',
                }}
              >
                <div
                  className='spinner-border'
                  style={{color: '#6366f1', width: 28, height: 28, borderWidth: 3}}
                />
              </div>
              <p className='text-muted mb-0'>{t('Loading your listings')}…</p>
            </div>
          ) : availableCategoryIds.length === 0 ? (
            <div className='d-flex flex-column align-items-center py-16 gap-3'>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className='fas fa-store fs-1 text-muted' />
              </div>
              <p className='text-muted mb-0 fs-6'>
                {t('No listings found for this vendor.')}
              </p>
            </div>
          ) : (
            <div>
              {/* Step 1: Business Type */}
              {availableCategoryIds.length > 1 && (
                <div
                  className='mb-8'
                  style={{animation: 'fadeSlideIn 0.3s ease'}}
                >
                  <div className='d-flex align-items-center gap-2 mb-4'>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#6366f1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <span style={{color: 'white', fontSize: 11, fontWeight: 700}}>1</span>
                    </div>
                    <h6 className='mb-0 fw-bold' style={{color: '#374151', fontSize: 14}}>
                      {t('Select Business Type')}
                    </h6>
                  </div>

                  <div className='row g-3'>
                    {availableCategoryIds.map((categoryId) => {
                      const config = CATEGORY_CONFIG[categoryId]
                      const isSelected = selectedCategoryId === categoryId
                      const isHovered = hoveredCategory === categoryId
                      return (
                        <div className='col-md-3 col-sm-6' key={categoryId}>
                          <div
                            className='listing-ads-cat-card'
                            onClick={() => handleCategoryChange(categoryId)}
                            onMouseEnter={() => setHoveredCategory(categoryId)}
                            onMouseLeave={() => setHoveredCategory(null)}
                            style={{
                              cursor: 'pointer',
                              borderRadius: 14,
                              padding: '18px 16px',
                              border: isSelected
                                ? `2px solid ${config.color}`
                                : `2px solid ${isHovered ? '#d1d5db' : '#e5e7eb'}`,
                              background: isSelected
                                ? config.bgColor
                                : isHovered
                                  ? '#fafafa'
                                  : 'white',
                              boxShadow: isSelected
                                ? `0 6px 24px ${config.color}28`
                                : isHovered
                                  ? '0 4px 16px rgba(0,0,0,0.07)'
                                  : 'none',
                              position: 'relative',
                            }}
                          >
                            {isSelected && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 10,
                                  right: 10,
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  background: config.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <i
                                  className='fas fa-check text-white'
                                  style={{fontSize: 9}}
                                />
                              </div>
                            )}
                            <div
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: 11,
                                background: isSelected ? config.color : config.bgColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 12,
                                transition: 'background 0.2s',
                              }}
                            >
                              <i
                                className={config.icon}
                                style={{
                                  color: isSelected ? 'white' : config.color,
                                  fontSize: 18,
                                }}
                              />
                            </div>
                            <div
                              className='fw-bold'
                              style={{
                                color: isSelected ? config.color : '#111827',
                                fontSize: 14,
                                marginBottom: 3,
                              }}
                            >
                              {t(config.parentLabel)}
                            </div>
                            <div style={{color: '#9ca3af', fontSize: 11}}>{config.description}</div>
                            <div
                              style={{
                                color: isSelected ? config.color : '#9ca3af',
                                fontSize: 11,
                                marginTop: 4,
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            >
                              {parentLists[categoryId]?.length || 0} {t('listing(s)')}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Step 2: Select Property */}
              {selectedConfig && (
                <div
                  className='mb-8'
                  style={{animation: 'fadeSlideIn 0.3s ease'}}
                >
                  <div className='d-flex align-items-center gap-2 mb-4'>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: selectedConfig.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <span style={{color: 'white', fontSize: 11, fontWeight: 700}}>
                        {availableCategoryIds.length > 1 ? '2' : '1'}
                      </span>
                    </div>
                    <h6 className='mb-0 fw-bold' style={{color: '#374151', fontSize: 14}}>
                      {t('Select')} {t(selectedConfig.parentLabel)}
                    </h6>
                    <span
                      style={{
                        background: selectedConfig.bgColor,
                        color: selectedConfig.color,
                        fontSize: 11,
                        padding: '2px 10px',
                        borderRadius: 20,
                        fontWeight: 600,
                      }}
                    >
                      {selectedParentList.length} {t('available')}
                    </span>
                  </div>

                  <div className='row g-4'>
                    {selectedParentList.map((item: any) => {
                      const parentId = String(item[selectedConfig.parentIdKey])
                      const isSelected = selectedParentId === parentId
                      const isHovered = hoveredParent === parentId
                      const imageUrl = getThumbImage(item)
                      const subcatName = getSubcategoryName(item)

                      return (
                        <div className='col-md-4 col-sm-6' key={parentId}>
                          <div
                            className='listing-ads-parent-card'
                            onClick={() => handleParentSelect(parentId)}
                            onMouseEnter={() => setHoveredParent(parentId)}
                            onMouseLeave={() => setHoveredParent(null)}
                            style={{
                              cursor: 'pointer',
                              borderRadius: 14,
                              overflow: 'hidden',
                              border: isSelected
                                ? `2px solid ${selectedConfig.color}`
                                : `2px solid ${isHovered ? '#d1d5db' : '#e5e7eb'}`,
                              background: 'white',
                              boxShadow: isSelected
                                ? `0 8px 28px ${selectedConfig.color}28`
                                : isHovered
                                  ? '0 6px 20px rgba(0,0,0,0.09)'
                                  : '0 2px 8px rgba(0,0,0,0.05)',
                            }}
                          >
                            {/* Image area */}
                            <div
                              style={{
                                height: 130,
                                position: 'relative',
                                overflow: 'hidden',
                                background: imageUrl ? 'transparent' : selectedConfig.bgColor,
                              }}
                            >
                              {imageUrl ? (
                                <img
                                  className='listing-ads-parent-img'
                                  src={imageUrl}
                                  alt={getDisplayName(item)}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.35s ease',
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <i
                                    className={selectedConfig.icon}
                                    style={{
                                      color: selectedConfig.color,
                                      fontSize: 36,
                                      opacity: 0.4,
                                    }}
                                  />
                                </div>
                              )}

                              {/* Selected badge */}
                              {isSelected && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    background: selectedConfig.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: `0 3px 10px ${selectedConfig.color}60`,
                                  }}
                                >
                                  <i className='fas fa-check text-white' style={{fontSize: 11}} />
                                </div>
                              )}

                              {/* Subcategory pill */}
                              {subcatName && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 10,
                                    background: 'rgba(0,0,0,0.55)',
                                    color: 'white',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    padding: '3px 9px',
                                    borderRadius: 20,
                                    backdropFilter: 'blur(4px)',
                                  }}
                                >
                                  {subcatName}
                                </div>
                              )}
                            </div>

                            {/* Card content */}
                            <div style={{padding: '13px 14px 14px'}}>
                              <div
                                className='fw-bold'
                                style={{
                                  color: isSelected ? selectedConfig.color : '#111827',
                                  fontSize: 14,
                                  marginBottom: 6,
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  transition: 'color 0.2s',
                                }}
                              >
                                {getDisplayName(item)}
                              </div>
                              <div className='d-flex align-items-center gap-3 flex-wrap'>
                                {item.noOfRooms !== undefined && (
                                  <span
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      color: '#6b7280',
                                      fontSize: 11,
                                    }}
                                  >
                                    <i className='fas fa-door-open' style={{fontSize: 10}} />
                                    {item.noOfRooms} {t('rooms')}
                                  </span>
                                )}
                                {item.noOfBooking !== undefined && (
                                  <span
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      color: '#6b7280',
                                      fontSize: 11,
                                    }}
                                  >
                                    <i className='fas fa-calendar-check' style={{fontSize: 10}} />
                                    {item.noOfBooking} {t('bookings')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Step 3: Child Items (selectable for vehicle/tour) */}
              {selectedConfig && selectedParentId && selectionMode === 'specific' && (
                <div style={{animation: 'fadeSlideIn 0.3s ease'}}>
                  <div className='d-flex align-items-center gap-2 mb-4'>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: selectedConfig.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <span style={{color: 'white', fontSize: 11, fontWeight: 700}}>
                        {availableCategoryIds.length > 1 ? '3' : '2'}
                      </span>
                    </div>
                    <h6 className='mb-0 fw-bold' style={{color: '#374151', fontSize: 14}}>
                      {t('Select')} {t(selectedConfig.childLabel)}
                    </h6>
                    {!isLoadingChildren && childList.length > 0 && (
                      <span
                        style={{
                          background: selectedConfig.bgColor,
                          color: selectedConfig.color,
                          fontSize: 11,
                          padding: '2px 10px',
                          borderRadius: 20,
                          fontWeight: 600,
                        }}
                      >
                        {selectedChildIds.length > 0 ? t('1 selected') : t('none selected')}
                      </span>
                    )}
                  </div>

                  {isLoadingChildren ? (
                    <div className='d-flex align-items-center justify-content-center gap-3 py-10'>
                      <span
                        className='spinner-border spinner-border-sm'
                        style={{color: selectedConfig.color}}
                      />
                      <span className='text-muted'>{t('Loading')}…</span>
                    </div>
                  ) : childList.length === 0 ? (
                    <div className='d-flex flex-column align-items-center py-10 gap-2'>
                      <i className='fas fa-inbox fs-2x text-muted' />
                      <p className='text-muted mb-0'>
                        {t(`No ${selectedConfig.childLabel.toLowerCase()} found`)}
                      </p>
                    </div>
                  ) : (
                    <div className='row g-3'>
                      {childList.map((item: any) => {
                        const childId = String(item[selectedConfig.childIdKey])
                        const isChecked = selectedChildIds.includes(childId)
                        return (
                          <div className='col-md-4 col-sm-6' key={childId}>
                            <div
                              className='listing-ads-child-card'
                              onClick={() => handleChildToggle(childId)}
                              style={{
                                cursor: 'pointer',
                                borderRadius: 11,
                                padding: '13px 15px',
                                border: isChecked
                                  ? `2px solid ${selectedConfig.color}`
                                  : '2px solid #e5e7eb',
                                background: isChecked ? selectedConfig.bgColor : 'white',
                                boxShadow: isChecked
                                  ? `0 4px 14px ${selectedConfig.color}20`
                                  : '0 2px 6px rgba(0,0,0,0.04)',
                              }}
                            >
                              <div className='d-flex align-items-center gap-3'>
                                <div
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    border: isChecked
                                      ? `6px solid ${selectedConfig.color}`
                                      : '2px solid #d1d5db',
                                    background: 'white',
                                    transition: 'border 0.18s',
                                  }}
                                />
                                <span
                                  className='fw-semibold'
                                  style={{
                                    color: isChecked ? selectedConfig.color : '#374151',
                                    fontSize: 13,
                                    transition: 'color 0.18s',
                                  }}
                                >
                                  {getDisplayName(item)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className='d-flex justify-content-end gap-3 align-items-center mt-6'>
        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => navigate('/viewsponsuredadvertisment')}
        >
          <i className='fas fa-arrow-left me-2' />
          {t('Back')}
        </button>

        <button
          type='button'
          className='btn btn-primary'
          onClick={handleSubmit}
          disabled={isSubmitting || isLoadingParents || availableCategoryIds.length === 0}
        >
          {isSubmitting ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              {t('Saving...')}
            </>
          ) : (
            <>
              {t('Save & Continue')}
              <i className='fas fa-arrow-right ms-2' />
            </>
          )}
        </button>
      </div>
    </>
  )
}

export default AddListingAds
